import { useState, type ComponentPropsWithRef, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { Tooltip } from "radix-ui";
import { cx } from "./cx";

export type ButtonVariant = "default" | "primary" | "danger" | "dangerPrimary" | "ghost";
export type ButtonSize = "md" | "sm" | "xs";

const VARIANTS: Record<ButtonVariant, string> = {
  default: "bg-bg border-border text-fg not-aria-disabled:hover:bg-surface-2",
  primary: "bg-primary border-primary text-on-primary not-aria-disabled:hover:brightness-110",
  danger: "bg-bg border-border text-attention not-aria-disabled:hover:bg-surface-2",
  dangerPrimary: "bg-attention border-attention text-on-attention not-aria-disabled:hover:brightness-110",
  ghost:
    "border-transparent bg-transparent text-muted not-aria-disabled:hover:bg-surface-2 not-aria-disabled:hover:text-fg",
};

const SIZES: Record<ButtonSize, string> = {
  md: "h-8 px-3.5 text-base rounded-lg",
  sm: "h-7 px-2.5 text-md rounded-lg",
  xs: "h-6 px-2 text-md rounded-md",
};

export function buttonClass(variant: ButtonVariant = "default", size: ButtonSize = "md", className?: string): string {
  return cx(
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap border font-strong aria-disabled:opacity-50 [&_svg]:size-3.5",
    VARIANTS[variant],
    SIZES[size],
    className,
  );
}

export interface ButtonProps extends ComponentPropsWithRef<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabledReason?: string | null | undefined;
}

export function Button({
  variant = "default",
  size = "md",
  disabledReason,
  className,
  type = "button",
  ...props
}: ButtonProps) {
  if (disabledReason) {
    return <DisabledButton reason={disabledReason} className={buttonClass(variant, size, className)} {...props} />;
  }
  return <button type={type} className={buttonClass(variant, size, className)} {...props} />;
}

interface DisabledButtonProps extends Omit<ComponentPropsWithRef<"button">, "onClick"> {
  reason: string;
}

function DisabledButton({ reason, children, onPointerDown, ...props }: DisabledButtonProps) {
  const [open, setOpen] = useState(false);
  const keepOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setOpen(true);
  };
  const keepOpenOnPointer = (event: PointerEvent<HTMLButtonElement>) => {
    onPointerDown?.(event);
    event.preventDefault();
  };
  return (
    <Tooltip.Root open={open} onOpenChange={setOpen} delayDuration={150}>
      <Tooltip.Trigger asChild>
        <button {...props} type="button" aria-disabled="true" onClick={keepOpen} onPointerDown={keepOpenOnPointer}>
          {children}
        </button>
      </Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="top"
          sideOffset={6}
          collisionPadding={8}
          className="z-tooltip max-w-[260px] rounded-md bg-inverse px-2.5 py-1.5 text-sm leading-snug text-on-inverse shadow-pop"
        >
          {reason}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

export interface IconButtonProps extends ComponentPropsWithRef<"button"> {
  label: string;
  size?: "md" | "sm";
}

export function IconButton({ label, size = "md", className, type = "button", ...props }: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      className={cx(
        "inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-border bg-bg text-muted hover:bg-surface-2 hover:text-fg aria-expanded:bg-surface-3 aria-expanded:text-fg aria-[current=page]:bg-surface-3 aria-[current=page]:text-fg [&_svg]:size-3.5",
        size === "md" ? "h-8 min-w-8 px-2" : "h-7 min-w-7 px-2 text-md",
        className,
      )}
      {...props}
    />
  );
}

export function TooltipProvider({ children }: { children: ReactNode }) {
  return <Tooltip.Provider delayDuration={300}>{children}</Tooltip.Provider>;
}
